import { FocusNode } from '@please/lrud'
import styled, { css } from 'styled-components'

import { ContentUnavailableElem } from '@/components/ContentUnavailable/styles'
import { ScrollContainerType } from '@/types/sliderTypes'

import {
  matchRowHeight,
  maxMatchesTableHeight,
  rowMarginBottom,
  standingsRowHeight,
} from './constants'

const focusLeafMixin = (color: string, width = 4) => `
  &.isFocusedLeaf {
    box-shadow: 0 0 0 ${width}px ${color};
  }
`
export const DataWidgetElem = styled.div`
  display: flex;
  margin-bottom: 50px;
  margin-right: 112px;
  flex-direction: column;
`
export const ContentUnavailableWrapper = styled.div`
  height: ${maxMatchesTableHeight}px;
`
export const TableHeader = styled.div`
  display: flex;
  width: 100%;
  height: ${standingsRowHeight}px;
`
export const Column = styled.div<{
  $width: number
  $accent?: boolean
}>`
  font-weight: 400;
  font-size: 20px;
  padding: 0 2px;
  text-align: center;
  width: ${({ $width }) => $width + 'px'};
  color: ${({ $accent, theme }) =>
    $accent ? theme.colors.dataWidget.accent : theme.colors.dataWidget.text};
  overflow: hidden;
`
export const TableRowWrapper = styled(FocusNode)<{
  $firstColHighlighted?: boolean
  $firstColHighlightColor?: number
  $tall?: boolean
  $rowHighlighted?: boolean
}>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.dataWidget.background};
  margin-bottom: ${rowMarginBottom}px;
  height: ${({ $tall }) => ($tall ? matchRowHeight : standingsRowHeight)}px;
  line-height: 50px;

  ${({ $rowHighlighted }) =>
    $rowHighlighted &&
    css`
      box-shadow: rgb(255, 255, 255) 0 0 0 2px inset;
    `}

  &:last-child {
    margin-bottom: 0;
  }

  &.isFocusedLeaf {
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.accent};
  }

  ${({ $firstColHighlighted, $firstColHighlightColor = 4, theme }) =>
    $firstColHighlighted &&
    `
    ${Column}:first-of-type {
      background-color: ${theme.colors.dataWidget.standingsPositionColors[$firstColHighlightColor]};
    }`}
`
export const CellWithIcon = styled.div<{
  $isRevert?: boolean
  $tall?: boolean
}>`
  display: flex;
  align-items: center;
  width: 100%;
  flex-direction: ${({ $isRevert }) => ($isRevert ? 'row-reverse' : 'row')};
`

export const PointsCell = styled.div`
  color: ${({ theme }) => theme.colors.dataWidget.accent};
`

export const Title = styled.div`
  font-weight: 500;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`
export const ColumnHeader = styled(Column)`
  font-weight: 500;
  font-size: 18px;
  text-transform: uppercase;
  background: none !important;
  color: ${({ theme }) => theme.colors.dataWidget.text}!important;

  &:nth-of-type(2) {
    justify-content: flex-start;
    padding: 0 12px;
  }
`
export const Icon = styled.img`
  height: 32px;
  width: 32px;
  object-fit: contain;
  margin: 0 10px;
`
export const TimeDescription = styled.div`
  position: absolute;
  left: 24px;
  top: 50%;
  transform: translateY(-50%);
`
export const DateContainer = styled.div`
  font-weight: 500;
  font-size: 18px;
  line-height: 21px;
  color: ${({ theme }) => theme.colors.dataWidget.text};
  opacity: 0.9;
  margin-bottom: 5px;
`
export const Time = styled.div`
  font-weight: 500;
  font-size: 22px;
  line-height: 26px;
  color: ${({ theme }) => theme.colors.dataWidget.accent};
`
export const TimeWrapper = styled.div`
  display: flex;
  align-items: center;

  ${Time} {
    margin-right: 10px;
  }
`

export const LabelLive = styled.div`
  height: 18px;
  width: 38px;
  background: ${({ theme }) => theme.colors.dataWidget.liveBg};
  font-weight: 700;
  font-size: 12px;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.dataWidget.liveText};
  text-transform: uppercase;
  text-align: center;
`
export const TimeLabel = styled.div<{
  $isLive: boolean
}>`
  position: absolute;
  top: 0;
  left: 0;
  height: 28px;
  line-height: 28px;
  background: ${({ theme }) => theme.colors.dataWidget.liveBg};
  color: ${({ theme }) => theme.colors.dataWidget.liveText};
  font-weight: 700;
  font-size: 16px;
  text-transform: uppercase;
  padding: 0 0.1rem 0 0.4rem;
  &:after {
    position: absolute;
    content: '';
    top: 0;
    left: 100%;
    border-top: 28px solid ${(props) => props.theme.colors.tiles.liveBg};
    border-right: 23px solid transparent;
  }
  &:before {
    ${({ $isLive }) =>
      $isLive &&
      css`
        content: '';
        display: inline-block;
        vertical-align: middle;
        width: 6px;
        height: 6px;
        background: red;
        border-radius: 50%;
        margin: 0 0.25rem 0.05rem 0.2rem;
        animation: blinking 1s infinite alternate;
      `}
  }
  @keyframes blinking {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }
`
export const StandingsLeagueHeaderRow = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: space-between;
`
export const StandingsLeagueHeaderColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 776px;
`
export const StandingsLeagueColumn = styled(FocusNode)`
  display: flex;
  flex-direction: column;
  width: 776px;
`
export const StandingsLeagueScrollContainer = styled.div<ScrollContainerType>`
  position: relative;
  height: ${({ height }) => (height ? `${height}px` : `100%`)};
  overflow: hidden;
  width: 100%;
  display: flex;
  justify-content: space-between;
`
export const MatchesTable = styled(FocusNode)`
  width: 1584px;
  position: relative;

  & ::-webkit-scrollbar {
    width: 8px;
    background: rgba(255, 255, 255, 0.1);
  }

  & ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }

  & ::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.title};
  }

  ${Column} {
    &:nth-of-type(2) {
      font-weight: 500;
      font-size: 40px;
    }
  }

  ${Title} {
    font-size: 24px;
  }

  ${CellWithIcon} {
    img {
      width: 60px;
      height: 60px;
    }
  }
`

export const MatchesScrollContainer = styled.div<{
  $fixedHeight: boolean
}>`
  width: calc(100% + 16px);
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 8px;
  position: absolute;
  height: ${({ $fixedHeight }) =>
    $fixedHeight ? `${maxMatchesTableHeight}px` : 'auto'};
  top: 0;
`

export const StandingsLeagueTable = styled(FocusNode)`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 1584px;
  ${({ theme }) => focusLeafMixin(theme.colors.dataWidget.focusedElem.border)}
`

export const MatchesWrapper = styled(FocusNode)`
  ${({ theme }) => focusLeafMixin(theme.colors.dataWidget.focusedElem.border)}
`

export const WeekSwitcherWrapper = styled(FocusNode)`
  display: flex;
  align-items: center;
  height: 48px;
  margin-bottom: 20px;
`

export const WeekSwitcherDateWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 0 20px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  background-color: rgba(255, 255, 255, 0.1);
`

export const WeekSwitcherDateIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-right: 2px solid rgba(255, 255, 255, 0.1);
`

export const WeekSwitcherDateLabel = styled.div`
  font-size: 18px;
  line-height: 1;
  width: 200px;
  text-align: center;
`

export const WeekSwitcherButton = styled(FocusNode)<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  background-color: rgba(255, 255, 255, 0.1);

  ${({ theme }) =>
    focusLeafMixin(theme.colors.dataWidget.focusedElem.border, 2)}

  ${({ $disabled }) =>
    $disabled &&
    css`
      border-color: rgba(255, 255, 255, 0.1);

      ${Icon} {
        opacity: 0.2;
      }
    `}
`

const GroupsWrapperWidth = '1700px'
const GroupsItemWidth = '1200px'

export const GroupsWrapper = styled(FocusNode)`
  display: flex;
  width: ${GroupsWrapperWidth};
  overflow-x: hidden;
`

export const GroupItemWrapper = styled(FocusNode)<{ $isActive?: boolean }>`
  padding-right: 80px;
  &:last-child {
    padding-right: calc(${GroupsWrapperWidth} - ${GroupsItemWidth});
  }
`
export const GroupItem = styled(FocusNode)<{ $isActive: boolean }>`
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.3)};
  width: ${GroupsItemWidth};
  user-select: none;

  &:hover {
    opacity: 1;
  }

  ${TableRowWrapper} {
    cursor: pointer;
    pointer-events: ${({ $isActive }) => ($isActive ? 'auto' : 'none')};
  }
`

export const GroupItemHeader = styled.div`
  font-size: 48px;
  font-weight: 500;
  margin-bottom: 20px;
`

export const ScoreWrapper = styled.div`
  display: flex;
`
export const ScoreItem = styled.div<{ $stretch?: boolean }>`
  text-align: center;
  ${({ $stretch }) =>
    $stretch &&
    css`
      flex: 1;
    `}
`
export const AccentText = styled.span`
  color: ${({ theme }) => theme.colors.dataWidget.accent};
  font-weight: 400;
  padding-left: 8px;
`
export const CommentaryWrapper = styled(FocusNode)`
  position: relative;
  width: 100%;
  min-height: 563px;
  ${ContentUnavailableElem} {
    font-weight: 400;
    flex-direction: row;
  }
`
export const MatchesContainer = styled(FocusNode)`
  width: 1768px;
  margin-left: -72px;
  margin-bottom: 24px;
  ${ContentUnavailableWrapper} {
    margin-left: 72px;
    width: 1584px;
  }
`
export const MatchWrapper = styled.div`
  padding-right: 16px;
  &:first-child {
    padding-left: 72px;
  }
  &:last-child {
    padding-right: 112px;
  }
`

export const MatchesHorizontalScrollContainer = styled(FocusNode)`
  display: flex;
  position: relative;
  height: auto;
  width: 100%;
  overflow-x: hidden;
  ${TableRowWrapper} {
    position: relative;
    width: 1584px;
    margin-bottom: 0;
  }
  ${MatchWrapper}[data-active="false"] {
    ${TableRowWrapper} {
      opacity: 0.7;
    }
    ${Column} {
      ${Icon} {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 48px;
        height: 48px;
        -webkit-filter: drop-shadow(3px 0px 6px #000);
        filter: drop-shadow(03px 0px 6px #000);
      }
    }
    ${Column}:first-of-type {
      ${Icon} {
        right: 30px;
        z-index: 1;
      }
    }
    ${Column}:nth-of-type(3) {
      ${Icon} {
        right: 0px;
      }
    }
    ${TimeWrapper} {
      display: none;
    }
  }

  ${MatchWrapper}[data-active="true"] + ${MatchWrapper}[data-active="false"] {
    ${Column}:first-of-type {
      ${Icon} {
        left: 0;
      }
    }
    ${Column}:nth-of-type(3) {
      ${Icon} {
        left: 30px;
      }
    }
  }

  ${Column} {
    &:nth-of-type(2) {
      font-weight: 500;
      font-size: 40px;
    }
  }
  ${Title} {
    font-size: 24px;
  }
  ${Icon} {
    width: 64px;
    height: 64px;
  }
`
export const MatchCommentaryGroup = styled(FocusNode)`
  max-height: 647px;
  min-height: 607px;
  width: calc(1584px + 24px);
  overflow-y: auto;
  padding-bottom: 40px;
  & > div {
    width: 1584px;
    height: 100%;
    min-height: 607px;
    padding: 24px;
    background-color: ${({ theme }) => theme.colors.dataWidget.background};
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    margin-bottom: 40px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
  }

  &.isFocused::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.title};
  }
`
export const MessageTitle = styled.div``
export const MessageTime = styled.div`
  color: ${({ theme }) => theme.colors.dataWidget.accent};
  font-size: 20px;
  line-height: 2em;
`

export const MatchCommentaryRow = styled.div<{
  $isLargeSize: boolean
}>`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.dataWidget.background};
  align-items: flex-start;
  &:last-of-type {
    border-bottom: none;
  }

  ${Column} {
    text-align: left;
    font-size: ${({ $isLargeSize }) => ($isLargeSize ? '40px' : '24px')};
    line-height: 1.2em;
    font-weight: 500;
  }
  ${Column} {
    &:first-of-type,
    &:last-of-type {
      align-self: center;
    }
  }
  ${Icon} {
    margin: 0 auto;
    width: ${({ $isLargeSize }) => ($isLargeSize ? '128px' : '50px')};
    height: ${({ $isLargeSize }) => ($isLargeSize ? '128px' : '50px')};
  }
  ${MessageTitle} {
    text-transform: ${({ $isLargeSize }) =>
      $isLargeSize ? 'uppercase' : 'initial'};
  }
`
